import clsx from 'clsx'
import { useRef } from 'react'

import mainStyles from 'assets/css/main.module.scss'
import { ReactComponent as ChevronLeft } from 'assets/icons/chevron-left.svg'
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg'
import BottomArc from 'components/bottomArc/BottomArc'
import GridCardLayout from 'components/cardGridLayout/CardGridLayout'
import { Button } from 'components/common/button/Button'
import { FrameworkWheelPage } from 'components/interactiveNavigation/FrameworkWheel'
import KeyTasks from 'components/keyTasks/KeyTasks'
import NavBar from 'components/navBar/NavBar'
import PageBanner from 'components/pageBanner/PageBanner'
import { ReturnToLandingButton } from 'components/returnToLandingButton/ReturnToLandingButton'
import { SprintCard } from 'components/sprintCard/SprintCard'
import { TopReturnToLandingButton } from 'components/topReturnToLandingButton/TopReturnToLandingButton'
import useHardNavigate from 'hooks/useHardNavigate'
import { AnalyticsActionType } from 'types/common/analytics.type'
import { EVENTS, trackAnalytics } from 'utils/analytics'

import { CreateBubbles, CreateCards, CreateKeyInputs, CreateKeyOutputs, SprintCards } from './constants/CreateConstants'
import styles from './Create.module.scss'

const Create = () => {
  const keyTasksRef = useRef<HTMLDivElement>(null)
  const keyPrinciplesRef = useRef<HTMLDivElement>(null)
  const caseStudiesRef = useRef<HTMLDivElement>(null)

  const hardNavigate = useHardNavigate()

  return (
    <div className={styles.Assemble}>
      <TopReturnToLandingButton />
      <PageBanner
        heading="Create"
        description="Big thinking, brave dreaming, and boundary-pushing concepts come alive as we co-create, design, and validate ideas."
        showBubble
        bubbleText="Grab your sunglasses, the thinking is that bright"
        backgroundStyle={{ background: 'linear-gradient(180deg, #FCD69C 0%, #FFB71E 100%)' }}
        showNavigationCircle
        bigTextFillColor="#FAB856"
        page={FrameworkWheelPage.Create}
      />
      <NavBar
        items={[
          { key: 'key-tasks', title: 'Key Tasks', ref: keyTasksRef },
          { key: 'key-tasks', title: 'Guidance', ref: keyPrinciplesRef },
          { key: 'case-studies', title: 'Case Studies', ref: caseStudiesRef },
        ]}
      />
      <KeyTasks
        color="amber"
        ref={keyTasksRef}
        tasks={CreateBubbles}
        inputs={CreateKeyInputs}
        outputs={CreateKeyOutputs}
      />
      <div className="w-full bg-[#F4F4F4]">
        <GridCardLayout
          ref={keyPrinciplesRef}
          title="Guidance on Using a sprint-based approach"
          cards={CreateCards}
          titleDecoration="stars"
          decorationColor="amber"
        />
      </div>
      <div ref={caseStudiesRef} className={clsx(mainStyles.container)}>
        <h2 className="text-center text-3xl font-semibold mt-16 mb-4">Sprint Tier Classification</h2>
        <p className="text-center mb-10">
          This framework defines sprint tiers by complexity, scope, and decision-making needs, helping teams align
          expectations, allocate resources, and involve the right stakeholders.
        </p>
        <div className="flex justify-stretch">
          {SprintCards.map(el => {
            return (
              <div className="w-1/3 px-4 self-stretch">
                <SprintCard
                  svgIcon={el.icon}
                  size={el.size}
                  topBarClass={el.topBarClass}
                  color={el.color}
                  bullets={el.bullets}
                  link={el.link}
                />
              </div>
            )
          })}
        </div>
      </div>
      <div className={clsx(mainStyles.container)}>
        <div className="my-16 flex justify-center">
          <Button
            variant="navigationControlBlack"
            text="Go to Explore"
            iconLeft={<ChevronLeft />}
            className="mr-4"
            border
            onClick={() => {
              hardNavigate('page-explore')
              trackAnalytics({
                type: AnalyticsActionType.action,
                payload: EVENTS.ACTION.CREATE.TO_EXPLORE,
              })
            }}
          />
          <ReturnToLandingButton />
          <Button
            variant="navigationControlBlack"
            text="Go to Shape"
            iconRight={<ChevronRight />}
            className="ml-4"
            border
            onClick={() => {
              hardNavigate('page-shape')
              trackAnalytics({
                type: AnalyticsActionType.action,
                payload: EVENTS.ACTION.CREATE.TO_SHAPE,
              })
            }}
          />
        </div>
      </div>
      <BottomArc />
    </div>
  )
}

export default Create
